<template>
  <div ref="root">
    <!-- Main contact section -->
    <section id="contact-main" class="py-28 bg-white dark:bg-dark transition-colors duration-300" data-animate="contact" :class="{ 'is-visible': visible.contact }">
      <div class="container mx-auto px-4">
        <div class="grid grid-cols-1 lg:grid-cols-5 gap-12 max-w-6xl mx-auto">
          <!-- Left: intro + contact info -->
          <div class="lg:col-span-2 anim-el d1">
            <div class="lg:sticky lg:top-24">
              <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">
                {{ t('contact.hero.tagline') }}
              </p>
              <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">
                {{ t('contact.form.headline') }}
              </h2>
              <p class="text-lg text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">
                {{ t('contact.form.subheadline') }}
              </p>

              <div class="mt-8 rounded-2xl p-5 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
                <div class="flex items-start gap-3">
                  <div class="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <Check class="h-5 w-5 text-blue-500" />
                  </div>
                  <div class="min-w-0">
                    <p class="text-sm font-semibold text-gray-900 dark:text-white transition-colors duration-300 mb-1">
                      {{ t('contact.form.valueProposition') }}
                    </p>
                    <p class="text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">
                      {{ t('contact.form.reassurance') }}
                    </p>
                  </div>
                </div>
              </div>

              <div class="mt-8 flex flex-wrap gap-3 text-sm text-gray-500 dark:text-gray-400">
                <div class="inline-flex items-center gap-2 rounded-full px-3 py-1.5 bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.08] transition-colors duration-300">
                  <Check class="h-4 w-4 text-blue-500" />
                  <span>{{ t('contact.form.trustBadges.clients') }}</span>
                </div>
                <div class="inline-flex items-center gap-2 rounded-full px-3 py-1.5 bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.08] transition-colors duration-300">
                  <Check class="h-4 w-4 text-blue-500" />
                  <span>{{ t('contact.form.trustBadges.response') }}</span>
                </div>
              </div>

              <!-- Email info -->
              <div class="mt-10 rounded-2xl p-6 bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
                <div class="flex items-center gap-4">
                  <div class="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                    <Mail class="h-5 w-5 text-blue-500" />
                  </div>
                  <div class="min-w-0">
                    <p class="text-sm text-gray-500 dark:text-gray-400 mb-1 transition-colors duration-300">{{ t('contact.info.email.label') }}</p>
                    <a :href="'mailto:' + emailAddress" class="font-semibold text-gray-900 dark:text-white hover:text-blue-500 dark:hover:text-blue-400 transition-colors break-all">{{ emailAddress }}</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right: tabbed content -->
          <div class="lg:col-span-3 anim-el d2">
            <!-- Tab Navigation -->
            <div class="flex gap-2 mb-4 rounded-2xl p-1.5 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
              <button
                type="button"
                @click="activeTab = 'form'"
                class="flex-1 inline-flex items-center justify-center gap-2 px-3 py-3 rounded-xl font-semibold text-[13px] sm:text-sm transition-all duration-300 border"
                :class="activeTab === 'form'
                  ? 'bg-white dark:bg-white/[0.06] border-gray-300 dark:border-white/[0.12] text-gray-900 dark:text-white shadow-sm'
                  : 'bg-transparent border-transparent text-gray-500 dark:text-gray-400 hover:bg-white/60 dark:hover:bg-white/[0.02] hover:border-gray-200 dark:hover:border-white/[0.06]'"
              >
                <span class="w-9 h-9 rounded-lg flex items-center justify-center"
                  :class="activeTab === 'form' ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06]'"
                >
                  <Mail class="h-4 w-4" :class="activeTab === 'form' ? 'text-blue-500' : 'text-gray-400 dark:text-gray-500'" />
                </span>
                <span class="leading-tight text-center">{{ t('contact.tabs.form') }}</span>
              </button>

              <button
                type="button"
                @click="activeTab = 'schedule'"
                class="flex-1 inline-flex items-center justify-center gap-2 px-3 py-3 rounded-xl font-semibold text-[13px] sm:text-sm transition-all duration-300 border"
                :class="activeTab === 'schedule'
                  ? 'bg-white dark:bg-white/[0.06] border-gray-300 dark:border-white/[0.12] text-gray-900 dark:text-white shadow-sm'
                  : 'bg-transparent border-transparent text-gray-500 dark:text-gray-400 hover:bg-white/60 dark:hover:bg-white/[0.02] hover:border-gray-200 dark:hover:border-white/[0.06]'"
              >
                <span class="w-9 h-9 rounded-lg flex items-center justify-center"
                  :class="activeTab === 'schedule' ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06]'"
                >
                  <Calendar class="h-4 w-4" :class="activeTab === 'schedule' ? 'text-blue-500' : 'text-gray-400 dark:text-gray-500'" />
                </span>
                <span class="leading-tight text-center">{{ t('contact.tabs.schedule') }}</span>
              </button>

              <button
                type="button"
                @click="activeTab = 'quote'"
                class="flex-1 inline-flex items-center justify-center gap-2 px-3 py-3 rounded-xl font-semibold text-[13px] sm:text-sm transition-all duration-300 border"
                :class="activeTab === 'quote'
                  ? 'bg-white dark:bg-white/[0.06] border-gray-300 dark:border-white/[0.12] text-gray-900 dark:text-white shadow-sm'
                  : 'bg-transparent border-transparent text-gray-500 dark:text-gray-400 hover:bg-white/60 dark:hover:bg-white/[0.02] hover:border-gray-200 dark:hover:border-white/[0.06]'"
              >
                <span class="w-9 h-9 rounded-lg flex items-center justify-center"
                  :class="activeTab === 'quote' ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06]'"
                >
                  <Globe class="h-4 w-4" :class="activeTab === 'quote' ? 'text-blue-500' : 'text-gray-400 dark:text-gray-500'" />
                </span>
                <span class="leading-tight text-center">{{ t('contact.tabs.websiteQuote') }}</span>
              </button>
            </div>

            <!-- Panels -->
            <div class="rounded-2xl p-6 sm:p-8 bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
              <!-- Contact Form -->
              <div v-show="activeTab === 'form'">
                <form @submit.prevent="handleSubmit" class="space-y-5">
                  <div>
                    <label class="block text-sm font-medium mb-2 text-gray-900 dark:text-white transition-colors duration-300">
                      {{ t('contact.form.fields.email') }} <span class="text-red-500">*</span>
                    </label>
                    <input
                      v-model="formData.email"
                      type="email"
                      :placeholder="t('contact.form.fields.emailPlaceholder')"
                      class="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/[0.03] border border-gray-300 dark:border-white/[0.08] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-gray-400 dark:focus:border-white/20 transition-colors duration-300"
                      required
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-2 text-gray-900 dark:text-white transition-colors duration-300">
                      {{ t('contact.form.fields.company') }}
                    </label>
                    <input
                      v-model="formData.website"
                      type="text"
                      :placeholder="t('contact.form.fields.companyPlaceholder')"
                      class="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/[0.03] border border-gray-300 dark:border-white/[0.08] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-gray-400 dark:focus:border-white/20 transition-colors duration-300"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-2 text-gray-900 dark:text-white transition-colors duration-300">
                      {{ t('contact.form.fields.goal') }} <span class="text-red-500">*</span>
                    </label>
                    <select
                      v-model="formData.goal"
                      class="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/[0.03] border border-gray-300 dark:border-white/[0.08] text-gray-900 dark:text-white focus:outline-none focus:border-gray-400 dark:focus:border-white/20 transition-colors duration-300"
                      required
                    >
                      <option value="">{{ t('contact.form.fields.goalPlaceholder') }}</option>
                      <option value="create-website">{{ t('contact.form.fields.goalOptions.createWebsite') }}</option>
                      <option value="improve-website">{{ t('contact.form.fields.goalOptions.improveWebsite') }}</option>
                      <option value="increase-clients">{{ t('contact.form.fields.goalOptions.increaseClients') }}</option>
                      <option value="automation">{{ t('contact.form.fields.goalOptions.automation') }}</option>
                      <option value="marketing">{{ t('contact.form.fields.goalOptions.marketing') }}</option>
                      <option value="voice-agents">{{ t('contact.form.fields.goalOptions.voiceAgents') }}</option>
                      <option value="ai-assistant">{{ t('contact.form.fields.goalOptions.aiAssistant') }}</option>
                      <option value="maintenance">{{ t('contact.form.fields.goalOptions.maintenance') }}</option>
                      <option value="other">{{ t('contact.form.fields.goalOptions.other') }}</option>
                    </select>
                  </div>

                  <div>
                    <label class="block text-sm font-medium mb-2 text-gray-900 dark:text-white transition-colors duration-300">
                      {{ t('contact.form.fields.message') }}
                    </label>
                    <textarea
                      v-model="formData.message"
                      rows="4"
                      :placeholder="t('contact.form.fields.messagePlaceholder')"
                      class="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/[0.03] border border-gray-300 dark:border-white/[0.08] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-gray-400 dark:focus:border-white/20 transition-colors duration-300"
                    ></textarea>
                  </div>

                  <div v-if="submitStatus" class="text-sm rounded-xl p-4" :class="submitStatus === 'success' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'bg-red-500/10 text-red-600 dark:text-red-400'">
                    <p v-if="submitStatus === 'success'">{{ t('contact.form.success') }}</p>
                    <p v-else>{{ t('contact.form.error') }}</p>
                  </div>

                  <button
                    type="submit"
                    class="w-full inline-flex items-center justify-center h-14 px-8 bg-gray-900 dark:bg-white text-white dark:text-dark rounded-xl font-bold text-[15px] transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="isSubmitting"
                  >
                    <span>{{ isSubmitting ? t('contact.form.sending') : t('contact.form.submit') }}</span>
                    <Send v-if="!isSubmitting" class="ml-2.5 h-5 w-5" />
                    <Loader2 v-else class="ml-2.5 h-5 w-5 animate-spin" />
                  </button>

                  <p class="text-xs text-gray-500 dark:text-gray-500 text-center transition-colors duration-300">
                    {{ t('contact.form.ctaSubtext') }}
                  </p>
                </form>
              </div>

              <!-- Schedule -->
              <div v-show="activeTab === 'schedule'">
                <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-3 transition-colors duration-300">
                  {{ t('contact.tabs.schedule') }}
                </h3>
                <p class="text-sm text-gray-500 dark:text-gray-400 mb-6 transition-colors duration-300">
                  {{ t('contact.schedule.description') }}
                </p>
                <div class="rounded-2xl overflow-hidden border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.02] transition-colors duration-300">
                  <div id="my-cal-inline" style="min-height: 640px;"></div>
                </div>
              </div>

              <!-- Quote -->
              <div v-show="activeTab === 'quote'">
                <div class="space-y-8">
                  <div>
                    <QuoteGeneratorEmbedded @quote-complete="handleQuoteComplete" />
                  </div>

                  <div v-if="quoteSubmitStatus === 'success' && !showQuoteForm" class="quote-success-container rounded-2xl p-8 bg-blue-500/10 border border-blue-500/20">
                    <div class="text-center">
                      <div class="inline-flex items-center justify-center w-14 h-14 bg-blue-500 rounded-full mb-4">
                        <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      </div>
                      <h3 class="text-2xl font-bold mb-2 text-gray-900 dark:text-white transition-colors duration-300">{{ t('contact.quoteForm.successTitle') }}</h3>
                      <p class="text-base text-gray-700 dark:text-gray-300 mb-3 transition-colors duration-300">{{ t('contact.quoteForm.success') }}</p>
                      <p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ t('contact.quoteForm.successDescription') }}</p>
                    </div>
                  </div>

                  <div v-if="showQuoteForm && quoteSubmitStatus !== 'success'" class="quote-form-container rounded-2xl p-6 sm:p-8 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
                    <h3 class="text-xl font-bold mb-2 text-gray-900 dark:text-white transition-colors duration-300">{{ t('contact.quoteForm.title') }}</h3>
                    <p class="text-sm text-gray-500 dark:text-gray-400 mb-6 transition-colors duration-300">{{ t('contact.quoteForm.description') }}</p>

                    <form @submit.prevent="handleQuoteSubmit" class="space-y-5">
                      <div>
                        <label class="block text-sm font-medium mb-2 text-gray-900 dark:text-white transition-colors duration-300">
                          {{ t('contact.quoteForm.fields.name') }} <span class="text-red-500">*</span>
                        </label>
                        <input
                          v-model="quoteFormData.name"
                          type="text"
                          :placeholder="t('contact.quoteForm.fields.namePlaceholder')"
                          class="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/[0.03] border border-gray-300 dark:border-white/[0.08] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-gray-400 dark:focus:border-white/20 transition-colors duration-300"
                          required
                        />
                      </div>

                      <div>
                        <label class="block text-sm font-medium mb-2 text-gray-900 dark:text-white transition-colors duration-300">
                          {{ t('contact.quoteForm.fields.email') }} <span class="text-red-500">*</span>
                        </label>
                        <input
                          v-model="quoteFormData.email"
                          type="email"
                          :placeholder="t('contact.quoteForm.fields.emailPlaceholder')"
                          class="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/[0.03] border border-gray-300 dark:border-white/[0.08] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-gray-400 dark:focus:border-white/20 transition-colors duration-300"
                          required
                        />
                      </div>

                      <div>
                        <label class="block text-sm font-medium mb-2 text-gray-900 dark:text-white transition-colors duration-300">
                          {{ t('contact.quoteForm.fields.message') }}
                        </label>
                        <textarea
                          v-model="quoteFormData.message"
                          rows="4"
                          :placeholder="t('contact.quoteForm.fields.messagePlaceholder')"
                          class="w-full px-4 py-3 rounded-xl bg-white dark:bg-white/[0.03] border border-gray-300 dark:border-white/[0.08] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-gray-400 dark:focus:border-white/20 transition-colors duration-300"
                        ></textarea>
                      </div>

                      <div v-if="quoteSubmitStatus === 'error'" class="text-sm rounded-xl p-4 bg-red-500/10 text-red-600 dark:text-red-400">
                        <p>{{ t('contact.quoteForm.error') }}</p>
                      </div>

                      <button
                        type="submit"
                        class="w-full inline-flex items-center justify-center h-14 px-8 bg-gray-900 dark:bg-white text-white dark:text-dark rounded-xl font-bold text-[15px] transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        :disabled="quoteIsSubmitting"
                      >
                        <span>{{ quoteIsSubmitting ? t('contact.quoteForm.sending') : t('contact.quoteForm.submit') }}</span>
                        <Send v-if="!quoteIsSubmitting" class="ml-2.5 h-5 w-5" />
                        <Loader2 v-else class="ml-2.5 h-5 w-5 animate-spin" />
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300" data-animate="faq" :class="{ 'is-visible': visible.faq }">
      <div class="container mx-auto px-4">
        <div class="grid grid-cols-1 lg:grid-cols-5 gap-12 max-w-6xl mx-auto">
          <div class="lg:col-span-2 anim-el d1">
            <div class="lg:sticky lg:top-24">
              <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ t('contact.faq.title') }}</p>
              <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">
                {{ t('contact.faq.title') }}
              </h2>
              <button
                type="button"
                @click="activeTab = 'form'; scrollToMain()"
                class="group inline-flex items-center h-12 px-6 bg-gray-900 dark:bg-white text-white dark:text-dark rounded-xl font-bold transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
              >
                <Mail class="mr-2 h-5 w-5" />
                {{ t('contact.form.submit') }}
              </button>
            </div>
          </div>
          <div class="lg:col-span-3 anim-el d2">
            <div class="space-y-3">
              <div
                v-for="(faq, index) in contactFaqs"
                :key="index"
                class="rounded-xl border transition-all duration-300"
                :class="activeContactFaq === index
                  ? 'bg-white dark:bg-white/[0.04] border-gray-300 dark:border-white/[0.12] shadow-sm'
                  : 'bg-transparent border-gray-200 dark:border-white/[0.06] hover:border-gray-300 dark:hover:border-white/[0.1]'"
              >
                <button type="button" @click="toggleContactFaq(index)" class="w-full flex justify-between items-center p-5 text-left">
                  <h3 class="text-[15px] font-semibold text-gray-900 dark:text-white pr-8 transition-colors duration-300">{{ faq.question }}</h3>
                  <div class="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300" :class="activeContactFaq === index ? 'bg-blue-500/10' : 'bg-gray-100 dark:bg-white/[0.06]'">
                    <ChevronDown class="h-4 w-4 flex-shrink-0 transition-all duration-300" :class="activeContactFaq === index ? 'rotate-180 text-blue-500' : 'text-gray-400 dark:text-gray-500'" />
                  </div>
                </button>
                <div v-show="activeContactFaq === index" class="px-5 pb-5 -mt-1">
                  <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ faq.answer }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, computed, watch, nextTick } from 'vue'
import { Send, Loader2, ChevronDown, Mail, Calendar, Globe, Check } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import QuoteGeneratorEmbedded from './QuoteGeneratorEmbedded.vue'
const { t, tm } = useI18n()
const route = useRoute()

const root = ref(null)
const visible = reactive({})
let observer = null

const activeTab = ref('form')
const isSubmitting = ref(false)
const submitStatus = ref(null)

const formData = ref({
    email: '',
    website: '',
    goal: '',
    message: ''
})

const activeContactFaq = ref(null)

const toggleContactFaq = (index) => {
  activeContactFaq.value = activeContactFaq.value === index ? null : index
}

const syncTabFromRoute = () => {
  const tab = route.query?.tab
  if (tab === 'form' || tab === 'schedule' || tab === 'quote') {
    activeTab.value = tab
  }
}

const scrollToMain = () => {
  const el = document.getElementById('contact-main')
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const contactFaqs = computed(() => {
  const questions = tm('contact.faq.questions')
  return Array.isArray(questions) ? questions : []
})

// Initialize Cal.com
const initCal = () => {
    try {
        if (typeof window !== 'undefined' && window.Cal && window.Cal.ns && window.Cal.ns["15min"]) {
            // Cal.com already initialized, just render inline
            window.Cal.ns["15min"]("inline", {
                elementOrSelector: "#my-cal-inline",
                config: {
                    "layout": "month_view",
                    "theme": "light"
                },
                calLink: "ciobai/15min",
            })
        }
    } catch (error) {
        console.error('Error initializing Cal.com inline:', error)
    }
}

// Add Cal.com initialization
onMounted(() => {
    syncTabFromRoute()

    observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) visible[entry.target.dataset.animate] = true
      })
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' })

    const nodes = root.value?.querySelectorAll?.('[data-animate]') || []
    nodes.forEach(el => observer.observe(el))

    try {
        /* eslint-disable */
        (function (C, A, L) {
            let p = function (a, ar) { a.q.push(ar); }
            let d = C.document
            C.Cal = C.Cal || function () {
                let cal = C.Cal
                let ar = arguments
                if (!cal.loaded) {
                    cal.ns = {}
                    cal.q = cal.q || []
                    d.head.appendChild(d.createElement("script")).src = A
                    cal.loaded = true
                }
                if (ar[0] === L) {
                    const api = function () { p(api, arguments) }
                    const namespace = ar[1]
                    api.q = api.q || []
                    if (typeof namespace === "string") {
                        cal.ns[namespace] = cal.ns[namespace] || api
                        p(cal.ns[namespace], ar)
                        p(cal, ["initNamespace", namespace])
                    } else p(cal, ar)
                    return
                }
                p(cal, ar)
            }
        })(window, "https://app.cal.com/embed/embed.js", "init")

        if (typeof window !== 'undefined' && window.Cal) {
            window.Cal("init", "15min", {origin:"https://cal.com"})

            if (window.Cal.ns && window.Cal.ns["15min"]) {
                window.Cal.ns["15min"]("ui", {
                    "theme": "light",
                    "styles": {
                        "branding": {
                            "brandColor": "#3b82f6"
                        }
                    },
                    "hideEventTypeDetails": false,
                    "layout": "month_view"
                })
            }
        }

        // Initialize Cal.com when schedule tab is active
        if (activeTab.value === 'schedule') {
            setTimeout(() => {
                initCal()
            }, 500)
        }
        /* eslint-enable */
    } catch (error) {
        console.error('Error initializing Cal.com:', error)
    }
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

watch(() => route.query?.tab, () => {
  syncTabFromRoute()
})

// Watch for tab changes to initialize Cal.com when schedule tab is selected
watch(activeTab, (newTab) => {
    if (newTab === 'schedule') {
        nextTick(() => {
            setTimeout(() => {
                initCal()
            }, 300)
        })
    }
})

const handleSubmit = async () => {
    try {
        isSubmitting.value = true
        const response = await fetch('https://formspree.io/f/mkgvzeed', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(formData.value)
        })

        if (response.ok) {
            submitStatus.value = 'success'
            formData.value = {
                email: '',
                website: '',
                goal: '',
                message: ''
            }
        } else {
            throw new Error('Form submission failed')
        }
    } catch (error) {
        submitStatus.value = 'error'
        console.error('Form submission error:', error)
    } finally {
        isSubmitting.value = false
        setTimeout(() => {
            submitStatus.value = null
        }, 3000)
    }
}

const emailAddress = computed(() => 'alex@ciobai.ch')

// Quote Generator Form State
const showQuoteForm = ref(false)
const quoteIsSubmitting = ref(false)
const quoteSubmitStatus = ref(null)
const quoteFormData = ref({
    name: '',
    email: '',
    message: ''
})
const quoteData = ref(null)

const handleQuoteComplete = (data) => {
    quoteData.value = data
    showQuoteForm.value = true
    // Scroll to form
    setTimeout(() => {
        const formElement = document.querySelector('.quote-form-container')
        if (formElement) {
            formElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    }, 300)
}

const handleQuoteSubmit = async () => {
    quoteIsSubmitting.value = true
    quoteSubmitStatus.value = null
    
    try {
        const response = await fetch('https://formspree.io/f/mkgvzeed', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                type: 'website-quote',
                ...quoteFormData.value,
                quoteData: quoteData.value
            })
        })

        if (response.ok) {
            quoteSubmitStatus.value = 'success'
            quoteFormData.value = {
                name: '',
                email: '',
                message: ''
            }
            showQuoteForm.value = false
            quoteData.value = null
            // Scroll to success message
            setTimeout(() => {
                const successElement = document.querySelector('.quote-success-container')
                if (successElement) {
                    successElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
            }, 100)
        } else {
            throw new Error('Form submission failed')
        }
    } catch (error) {
        quoteSubmitStatus.value = 'error'
        console.error('Quote form submission error:', error)
    } finally {
        quoteIsSubmitting.value = false
        // Keep success message visible longer (10 seconds)
        if (quoteSubmitStatus.value === 'success') {
            setTimeout(() => {
                quoteSubmitStatus.value = null
            }, 10000)
        } else {
            setTimeout(() => {
                quoteSubmitStatus.value = null
            }, 5000)
        }
    }
}
</script>

<style scoped>
/* Scroll-triggered entrance animations (same pattern as Website) */
.anim-el {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s cubic-bezier(.22,1,.36,1), transform 0.7s cubic-bezier(.22,1,.36,1);
}
.is-visible .anim-el {
  opacity: 1;
  transform: translateY(0);
}
.d1 { transition-delay: 0.05s; }
.d2 { transition-delay: 0.15s; }
.d3 { transition-delay: 0.25s; }
.d4 { transition-delay: 0.35s; }
.d5 { transition-delay: 0.45s; }
</style>